import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowUpRight, CalendarDays, ChevronLeft, ChevronRight, MapPin, Pause, Play,
} from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { formatDateTime } from "@/lib/format";
import { cn } from "@/lib/utils";
import type { EventRow } from "@/lib/types";

const AUTO_ADVANCE_MS = 6000;

interface EventsSpotlightProps {
  events: EventRow[];
  loading: boolean;
}

/**
 * Hero-style banner at the top of the Dashboard that surfaces upcoming
 * staff-portal events. Behaviour scales with how many events there are:
 *
 *   0 events → renders nothing (don't waste real estate).
 *   1 event  → single static slide, no controls.
 *   2+ events → carousel with prev/next arrows, dot indicators, pause /
 *               play toggle. Auto-advances every 6 s; pauses on hover.
 *
 * Slides use crossfade (opacity + scale) so the layout doesn't jitter on
 * advance. Each slide is wrapped in a <Link to={/events/<id>}> so the
 * whole banner is clickable.
 */
export function EventsSpotlight({ events, loading }: EventsSpotlightProps) {
  const [idx, setIdx]       = useState(0);
  const [paused, setPaused] = useState(false);

  // Reset to the first slide whenever the event list shape changes (e.g.
  // an event got published or deleted while the page was idle).
  useEffect(() => {
    if (idx >= events.length) setIdx(0);
  }, [events.length, idx]);

  // Auto-advance — only when there are ≥2 slides AND the user isn't
  // hovering. setInterval id captured so the cleanup actually cancels.
  useEffect(() => {
    if (events.length < 2 || paused) return;
    const t = setInterval(() => {
      setIdx((i) => (i + 1) % events.length);
    }, AUTO_ADVANCE_MS);
    return () => clearInterval(t);
  }, [events.length, paused]);

  if (loading) {
    return (
      <Card className="overflow-hidden border-border/60 shadow-soft">
        <Skeleton className="h-72 w-full sm:h-80 lg:h-[26rem]" />
      </Card>
    );
  }
  // Nothing scheduled — silently hide the banner. The "Upcoming events"
  // card lower down handles the "nothing scheduled" empty state already.
  if (!events.length) return null;

  const hasMany = events.length > 1;
  const active  = events[idx];

  return (
    <Card
      className="relative overflow-hidden border-border/60 shadow-soft"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      aria-roledescription="carousel"
      aria-label="Upcoming events spotlight"
    >
      {/* Slide stack — every slide rendered at the same absolute position,
          fading between them. Keeps height stable across advances. Taller
          on lg+ so it visually balances the side memories slideshow. */}
      <div className="relative h-72 sm:h-80 lg:h-[26rem]">
        {events.map((e, i) => (
          <SpotlightSlide
            key={e.id}
            event={e}
            visible={i === idx}
            ariaHidden={i !== idx}
          />
        ))}
      </div>

      {/* Carousel chrome — only when there's more than one slide. */}
      {hasMany && (
        <>
          <button
            type="button"
            onClick={() => setIdx((i) => (i - 1 + events.length) % events.length)}
            aria-label="Previous event"
            className="absolute left-3 top-1/2 z-10 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-white/20 bg-black/30 text-white backdrop-blur transition hover:bg-black/50"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => setIdx((i) => (i + 1) % events.length)}
            aria-label="Next event"
            className="absolute right-3 top-1/2 z-10 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-white/20 bg-black/30 text-white backdrop-blur transition hover:bg-black/50"
          >
            <ChevronRight className="h-4 w-4" />
          </button>

          {/* Bottom rail: dot indicators + pause / play toggle. */}
          <div className="absolute inset-x-0 bottom-3 z-10 flex items-center justify-center gap-2">
            {events.map((e, i) => (
              <button
                key={e.id}
                type="button"
                onClick={() => setIdx(i)}
                aria-label={`Jump to slide ${i + 1}`}
                aria-current={i === idx ? "true" : undefined}
                className={cn(
                  "h-1.5 rounded-full transition-all",
                  i === idx ? "w-6 bg-white" : "w-1.5 bg-white/50 hover:bg-white/70",
                )}
              />
            ))}
            <button
              type="button"
              onClick={() => setPaused((p) => !p)}
              aria-label={paused ? "Resume auto-advance" : "Pause auto-advance"}
              className="ml-1 flex h-5 w-5 items-center justify-center rounded-full bg-white/15 text-white transition hover:bg-white/25"
            >
              {paused
                ? <Play className="h-3 w-3 fill-current" />
                : <Pause className="h-3 w-3 fill-current" />}
            </button>
          </div>
        </>
      )}

      {/* Counter pill (top-right) — quick "1 / 5" cue when carousel is on. */}
      {hasMany && (
        <div className="absolute right-3 top-3 z-10 rounded-full bg-black/30 px-2 py-0.5 text-[10px] font-semibold text-white backdrop-blur">
          {idx + 1} / {events.length}
        </div>
      )}

      {/* Live region for screen readers — announces the active slide title. */}
      <div className="sr-only" aria-live="polite">
        {active.title}
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// One slide in the carousel. Image (or branded gradient fallback) backdrop
// + dark overlay + text content on top so titles stay legible.
// ---------------------------------------------------------------------------
function SpotlightSlide({
  event, visible, ariaHidden,
}: {
  event: EventRow;
  visible: boolean;
  ariaHidden: boolean;
}) {
  return (
    <Link
      to={`/events/${event.id}`}
      aria-hidden={ariaHidden}
      // The non-visible slides become inert: pointer-events off so a
      // user can't tab into a slide they can't see, opacity 0 so the
      // fade looks clean.
      className={cn(
        "absolute inset-0 transition-opacity duration-500 ease-out",
        visible
          ? "pointer-events-auto opacity-100"
          : "pointer-events-none opacity-0",
      )}
    >
      {/* Backdrop: image when present, MoPD-green gradient otherwise. */}
      {event.image_url ? (
        <img
          src={event.image_url}
          alt=""
          className="absolute inset-0 h-full w-full object-cover"
          loading="lazy"
        />
      ) : (
        <div className="absolute inset-0 bg-gradient-to-br from-primary via-emerald-700 to-emerald-900" />
      )}
      {/* Always-on dark vignette so light-text overlays read on any image. */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/20" />

      {/* Text content. */}
      <div className="relative z-[1] flex h-full flex-col justify-end p-5 sm:p-7 text-white">
        <Badge
          variant="outline"
          className="mb-3 w-fit border-white/30 bg-white/10 text-white backdrop-blur"
        >
          <CalendarDays className="mr-1 h-3 w-3" />
          {event.event_type_display}
        </Badge>
        <h2 className="line-clamp-2 text-xl font-bold tracking-tight sm:text-2xl">
          {event.title}
        </h2>
        {event.summary && (
          <p className="mt-1 line-clamp-2 max-w-2xl text-sm text-white/85">
            {event.summary}
          </p>
        )}
        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-white/90">
          <span className="flex items-center gap-1.5">
            <CalendarDays className="h-3.5 w-3.5" />
            {formatDateTime(event.start_at)}
          </span>
          {event.location && (
            <span className="flex items-center gap-1.5">
              <MapPin className="h-3.5 w-3.5" />
              {event.location}
            </span>
          )}
          {/* Always-visible call to action — the whole card is the link,
              but the button hints at clickability. */}
          <Button
            asChild
            size="sm"
            variant="secondary"
            className="ml-auto h-7 bg-white/95 text-foreground hover:bg-white"
          >
            <span>
              View event
              <ArrowUpRight className="ml-1 h-3 w-3" />
            </span>
          </Button>
        </div>
      </div>
    </Link>
  );
}
